import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, Lock, Smartphone, ArrowRight, ShieldCheck, Zap } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { user, login, loginDemo, loginWithToken } = useAuth();
  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [reqId, setReqId] = useState(null);
  const [demos, setDemos] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/");
    api.get("/auth/demo-accounts").then((r) => setDemos(r.data)).catch(() => {});
  }, [user, nav]);

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const reqOtp = async () => {
    setErr("");
    try {
      const { data } = await api.post("/auth/otp/request", { mobile });
      setDevOtp(data.dev_otp); setReqId(data.request_id); setOtp(data.dev_otp);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
  };

  const verifyOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { request_id: reqId, otp });
      loginWithToken(data); nav("/");
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="navy-grad hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1769376695235-d3253548a5d7?crop=entropy&cs=srgb&fm=jpg&q=85')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl grid place-items-center font-extrabold text-lg copper-glow" style={{ background: "var(--copper)" }}>IN</div>
            <div>
              <p className="font-display text-xl font-bold">INDIGO Partner Network</p>
              <p className="text-xs text-slate-400">Copper Tubes & Fittings · Mercure Metals & Alloys</p>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight">HVAC Partner,<br />Project Intelligence &<br /><span style={{ color: "var(--copper)" }}>Loyalty</span> Ecosystem</h1>
          <div className="mt-8 space-y-3 text-sm text-slate-300">
            {[["Partner 360 with unique IDs & digital cards", ShieldCheck],
              ["Duplicate engine, master-project attribution", Zap],
              ["Distributor RFQ→Dispatch & points ledger", ArrowRight]].map(([t, I], i) => (
              <div key={i} className="flex items-center gap-3"><I size={16} style={{ color: "var(--copper)" }} />{t}</div>
            ))}
          </div>
        </div>
        <div className="relative cl-badge inline-flex items-center gap-2 px-3 py-1.5 text-xs w-fit">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 pulse-dot" /> Powered by <b className="text-white">ChannelLoyalty.ai</b>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-lg grid place-items-center font-extrabold text-white" style={{ background: "var(--copper)" }}>IN</div>
            <p className="font-display font-bold text-lg">INDIGO Partner Network</p>
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Access the partner & project intelligence portal.</p>

          <div className="flex gap-2 mb-5 p-1 bg-slate-100 rounded-lg">
            <button data-testid="tab-password" onClick={() => setMode("password")} className={`flex-1 py-2 rounded-md text-sm font-medium ${mode === "password" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Email & Password</button>
            <button data-testid="tab-otp" onClick={() => setMode("otp")} className={`flex-1 py-2 rounded-md text-sm font-medium ${mode === "otp" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Partner Mobile OTP</button>
          </div>

          {err && <div className="mb-4 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

          {mode === "password" ? (
            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Email</label>
                <div className="relative mt-1">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required type="email"
                    className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--copper)]" placeholder="you@indigo.in" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Password</label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required type="password"
                    className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--copper)]" placeholder="••••••••" />
                </div>
              </div>
              <button data-testid="login-submit" disabled={busy} className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow disabled:opacity-60" style={{ background: "var(--copper)" }}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Mobile number</label>
                <div className="relative mt-1">
                  <Smartphone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="otp-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--copper)]" placeholder="9XXXXXXXXX" />
                </div>
              </div>
              {!reqId ? (
                <button data-testid="otp-request" onClick={reqOtp} className="w-full text-white font-semibold py-2.5 rounded-lg" style={{ background: "var(--navy-800)" }}>Send OTP</button>
              ) : (
                <>
                  {devOtp && <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-700" data-testid="otp-dev-panel">Dev panel (no live SMS): OTP is <b className="font-mono">{devOtp}</b></div>}
                  <input data-testid="otp-code" value={otp} onChange={(e) => setOtp(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono tracking-widest text-center" placeholder="______" />
                  <button data-testid="otp-verify" disabled={busy} onClick={verifyOtp} className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Verify & Sign in</button>
                </>
              )}
            </div>
          )}

          {/* Demo selector */}
          <div className="mt-8">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Demo accounts · one-tap sign in</p>
            <div className="grid grid-cols-2 gap-2" data-testid="demo-account-selector">
              {demos.map((d) => (
                <button key={d.email} data-testid={`demo-${(d.sub_role || d.role).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => loginDemo(d.email).then(() => nav("/"))}
                  className="text-left border border-slate-200 rounded-lg px-3 py-2 hover:border-[var(--copper)] hover:bg-orange-50/40 transition">
                  <p className="text-xs font-semibold text-slate-800 truncate">{d.sub_role}</p>
                  <p className="text-[10px] text-slate-400 truncate">{d.name}</p>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-3 text-center">Demo password: <span className="font-mono">demo123</span> · hidden in production</p>
          </div>
        </div>
      </div>
    </div>
  );
}
